import React from 'react';
import { Divider, Row, Col } from 'antd';
import { isNil } from 'lodash';

import {
  BreakInServiceEnum,
  CustomerAdditionalInfoResponseModel,
} from 'api/adminApiClient';
import BasePageCard from 'components/wrappers/BasePageCard';
import BaseSectionMessage from 'components/wrappers/BaseSectionMessage';
import { downloadFile } from 'utils/fileUtils';
import {
  getCommissionTypeApprovalLabel,
  getCustomerBreakInServiceLabel,
  getCustomerCostingMethodTypeLabel,
  getCustomerInvoicingCycleLabel,
  getCustomerPaymentTermsLabel,
  getCustomerPayrollCycleLabel,
  getCustomerPreScreeningBilledLabel,
} from 'utils/clientUtils';
import { areObjectValuesPresent } from 'utils/objectUtils';
import BaseAlert from 'components/wrappers/BaseAlert';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import {
  getCustomerCertificateOfInsuranceCreatedFile,
  getCustomerOnboardingDocumentationFile,
  getCustomerPurchaseOrderFile,
  getCustomerTempToPermConversionFileInfoForCustomer,
  getSOWTemplateFile,
} from 'redux/reducers/clientSlice';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { getWebsiteLink, getWebsiteName } from 'utils/urlUtils';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { formatNumberAsCurrency } from '../../../../utils/numberUtils';

type Props = {
  clientDetails: CustomerAdditionalInfoResponseModel;
};

export default function ClientAdditionalInfoSection(props: Props) {
  const { clientDetails } = props;
  const isAdminRole = useAppSelector(isAdminSelector);
  const {
    customerKey,
    customerId,
    generalContractInformation,
    financialInformation,
    additionalInfo3,
  } = clientDetails;
  const dispatch = useAppDispatch();

  const isFinancialOverviewVisible = areObjectValuesPresent(
    financialInformation?.financialOverview,
  );
  const isInsuranceRequirementsVisible = areObjectValuesPresent(
    generalContractInformation?.insuranceRequirements,
  );
  const isRateOverviewVisible = areObjectValuesPresent(
    financialInformation?.rateOverview,
  );
  const isMspOrVmsOverviewVisible = areObjectValuesPresent(
    financialInformation?.mspOrVmsOverview,
  );
  const isAssignmentRequirementsVisible = areObjectValuesPresent(
    additionalInfo3?.assignmentRequirements,
  );
  const isSpecialRequirementsVisible = areObjectValuesPresent(
    additionalInfo3?.specialAssignmentRequirements,
  );

  const isAdditionalInfo1Visible = isInsuranceRequirementsVisible;
  const isAdditionalInfo2Visible =
    isFinancialOverviewVisible ||
    isMspOrVmsOverviewVisible ||
    isRateOverviewVisible;

  const handleDownloadCustomerCertificateOfInsuranceFile = async () => {
    const result = await dispatch(
      getCustomerCertificateOfInsuranceCreatedFile({
        customerId: customerId as string,
        isAdminRole,
      }),
    );
    if (getCustomerCertificateOfInsuranceCreatedFile.fulfilled.match(result)) {
      const { downloadUrl, fileName } = result.payload;
      try {
        await downloadFile({ downloadUrl, fileName });
      } catch (e: any) {
        BaseAlert.error(e.message);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleDownloadTempToPermConversionFile = async () => {
    const result = await dispatch(
      getCustomerTempToPermConversionFileInfoForCustomer({
        customerId: customerId as string,
        isAdminRole,
      }),
    );
    if (
      getCustomerTempToPermConversionFileInfoForCustomer.fulfilled.match(result)
    ) {
      const { downloadUrl, fileName } = result.payload;
      try {
        await downloadFile({ downloadUrl, fileName });
      } catch (e: any) {
        BaseAlert.error(e.message);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleDownloadPurchaseOrderFile = async () => {
    if (!customerId) return;

    const result = await dispatch(getCustomerPurchaseOrderFile(customerId));

    if (getCustomerPurchaseOrderFile.fulfilled.match(result)) {
      try {
        await downloadFile({
          downloadUrl: result.payload.downloadUrl,
          fileName: result.payload.fileName,
        });
      } catch (e: any) {
        BaseAlert.error(e.message);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleDownloadOnboardingDocumentationFile = async (fileKey: string) => {
    if (!customerId) return;

    const result = await dispatch(
      getCustomerOnboardingDocumentationFile({
        customerId,
        fileKey,
        isAdminRole,
      }),
    );

    if (getCustomerOnboardingDocumentationFile.fulfilled.match(result)) {
      try {
        await downloadFile({
          downloadUrl: result.payload.downloadUrl,
          fileName: result.payload.fileName,
        });
      } catch (e: any) {
        BaseAlert.error(e.message);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleDownloadSOWTemplateFile = async () => {
    if (!customerId) return;

    const result = await dispatch(
      getSOWTemplateFile({ customerId, isAdminRole }),
    );

    if (getSOWTemplateFile.fulfilled.match(result)) {
      try {
        await downloadFile({
          downloadUrl: result.payload.downloadUrl,
          fileName: result.payload.fileName,
        });
      } catch (e: any) {
        BaseAlert.error(e.message);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  return (
    <Row gutter={[10, 10]}>
      {isAdminRole && !isAdditionalInfo1Visible && (
        <Col xs={24} md={12} className="d-flex">
          <BasePageCard
            className="stretch-card"
            title="General Contract Information"
            icons={<span className="icon icon-writing-tool"></span>}
          >
            <div className="content-wrapper">
              <BaseSectionMessage
                type="info"
                title="Info"
                message={
                  <span>
                    Please fill in the customer additional information.
                  </span>
                }
              />
            </div>
          </BasePageCard>
        </Col>
      )}

      {isAdditionalInfo1Visible && (
        <Col xs={24} md={12} className="d-flex">
          <BasePageCard
            className="stretch-card"
            title="General Contract Information"
            icons={<span className="icon icon-writing-tool"></span>}
          >
            <>
              {isInsuranceRequirementsVisible && (
                <>
                  <Divider className="no-margin" />

                  <div className="content-wrapper">
                    <Row gutter={[4, 4]} className="titled-sheet">
                      <Col xs={24}>
                        <div className="subtitle">Insurance requirements</div>
                      </Col>

                      {!!generalContractInformation?.insuranceRequirements
                        ?.certificateOfInsuranceSpecifications && (
                        <Col
                          xs={24}
                          md={12}
                          data-id="customerCertificateOfInsuranceSpecifications"
                        >
                          <div className="title">
                            Certificate of insurance specifications:
                          </div>
                          <div>
                            {
                              generalContractInformation?.insuranceRequirements
                                ?.certificateOfInsuranceSpecifications
                            }
                          </div>
                        </Col>
                      )}

                      {!!generalContractInformation?.insuranceRequirements
                        ?.submittedTo && (
                        <Col xs={24} md={12} data-id="customerSubmittedTo">
                          <div className="title">Submitted to:</div>
                          <div>
                            {
                              generalContractInformation?.insuranceRequirements
                                ?.submittedTo
                            }
                          </div>
                        </Col>
                      )}

                      {!!generalContractInformation?.insuranceRequirements
                        ?.certificateOfInsuranceCreatedFileKey && (
                        <Col
                          xs={24}
                          md={12}
                          data-id="customerDownloadCertificate"
                        >
                          <div className="title">
                            Download certificate of insurance created:
                          </div>
                          <div>
                            <span
                              className="link-imitation"
                              onClick={
                                handleDownloadCustomerCertificateOfInsuranceFile
                              }
                            >
                              {`Customer ${customerKey} certificate of insurance created`}
                            </span>
                          </div>
                        </Col>
                      )}

                      {!isNil(
                        generalContractInformation?.insuranceRequirements
                          ?.isCreditCheckVerified,
                      ) && (
                        <Col xs={24} md={12} data-id="customerSOWRequired">
                          <div className="title">Credit check verified:</div>
                          <div>
                            {generalContractInformation?.insuranceRequirements
                              ?.isCreditCheckVerified
                              ? 'Yes'
                              : 'No'}
                          </div>
                        </Col>
                      )}

                      {!isNil(
                        generalContractInformation?.insuranceRequirements
                          ?.sowRequired,
                      ) && (
                        <Col xs={24} md={12} data-id="customerSOWRequired">
                          <div className="title">SOW required:</div>
                          <div>
                            {generalContractInformation?.insuranceRequirements
                              ?.sowRequired
                              ? 'Yes'
                              : 'No'}
                          </div>
                        </Col>
                      )}

                      {!!generalContractInformation?.insuranceRequirements
                        ?.sowSpecialInstructions && (
                        <Col xs={24} md={12} data-id="customerSubmittedTo">
                          <div className="title">SOW special instructions:</div>
                          <div>
                            {
                              generalContractInformation?.insuranceRequirements
                                ?.sowSpecialInstructions
                            }
                          </div>
                        </Col>
                      )}

                      {!!generalContractInformation?.insuranceRequirements
                        ?.sowTemplateFileKey && (
                        <Col xs={24} md={12} data-id="customerSOWTemplate">
                          <div className="title">SOW template:</div>
                          <div>
                            <span
                              className="link-imitation"
                              onClick={handleDownloadSOWTemplateFile}
                            >
                              Customer {customerKey} SOW template
                            </span>
                          </div>
                        </Col>
                      )}
                    </Row>
                  </div>
                </>
              )}
            </>
          </BasePageCard>
        </Col>
      )}

      {isAdminRole && !isAdditionalInfo2Visible && (
        <Col xs={24} md={12} className="d-flex">
          <BasePageCard
            className="stretch-card"
            title="Financial Information"
            icons={<span className="icon icon-writing-tool"></span>}
          >
            <div className="content-wrapper">
              <BaseSectionMessage
                type="info"
                title="Info"
                message={
                  <span>
                    Please fill in the customer additional information.
                  </span>
                }
              />
            </div>
          </BasePageCard>
        </Col>
      )}

      {isAdditionalInfo2Visible && (
        <Col xs={24} md={12} className="d-flex">
          <BasePageCard
            className="stretch-card"
            title="Financial Information"
            icons={<span className="icon icon-writing-tool"></span>}
          >
            <>
              {isFinancialOverviewVisible && (
                <div className="content-wrapper">
                  <Row gutter={[4, 4]} className="titled-sheet">
                    <Col xs={24}>
                      <div className="subtitle">Financial overview</div>
                    </Col>

                    {!!financialInformation?.financialOverview
                      ?.creditCheckScore && (
                      <Col xs={24} md={12} data-id="customerCreditCheck">
                        <div className="title">Credit check:</div>
                        <div>
                          {
                            financialInformation?.financialOverview
                              ?.creditCheckScore
                          }
                        </div>
                      </Col>
                    )}

                    {!!financialInformation?.financialOverview
                      ?.commissionTypeApproval && (
                      <Col
                        xs={24}
                        md={12}
                        data-id="customerCommissionTypeApproval"
                      >
                        <div className="title">Commission type approval:</div>
                        <div>
                          {getCommissionTypeApprovalLabel(
                            financialInformation?.financialOverview
                              ?.commissionTypeApproval,
                          )}
                        </div>
                      </Col>
                    )}

                    {!!financialInformation?.financialOverview?.addedNotes && (
                      <Col xs={24} md={12} data-id="customerAddedNotes">
                        <div className="title">Added notes:</div>
                        <div>
                          {financialInformation?.financialOverview?.addedNotes}
                        </div>
                      </Col>
                    )}

                    {!!financialInformation?.financialOverview?.paymentTerm && (
                      <Col xs={24} md={12} data-id="customerPaymentTerms">
                        <div className="title">Payment terms:</div>
                        <div>
                          {getCustomerPaymentTermsLabel(
                            financialInformation?.financialOverview
                              ?.paymentTerm,
                          )}
                        </div>
                      </Col>
                    )}

                    {!isNil(
                      financialInformation?.financialOverview
                        ?.customerCostingMethodType,
                    ) && (
                      <Col xs={24} md={12}>
                        <div className="title">
                          Customer costing method type:
                        </div>
                        <div>
                          {getCustomerCostingMethodTypeLabel(
                            financialInformation?.financialOverview
                              ?.customerCostingMethodType,
                          )}
                        </div>
                      </Col>
                    )}
                  </Row>
                </div>
              )}

              {isRateOverviewVisible && (
                <div className="content-wrapper">
                  <Row gutter={[4, 4]} className="titled-sheet">
                    <Col xs={24}>
                      <div className="subtitle">Rate overview</div>
                    </Col>

                    {!isNil(
                      financialInformation?.rateOverview?.dtBillRateMultiplier,
                    ) && (
                      <Col
                        xs={24}
                        md={12}
                        data-id="customerDtBillRateMultiplier"
                      >
                        <div className="title">DT bill rate multiplier:</div>
                        <div>
                          {formatNumberAsCurrency({
                            num: financialInformation?.rateOverview
                              ?.dtBillRateMultiplier,
                          })}
                        </div>
                      </Col>
                    )}

                    {!!financialInformation?.rateOverview?.fixedMarkupRate && (
                      <Col xs={24} md={12} data-id="customerFixedMarkupRate">
                        <div className="title">Fixed markup rate:</div>
                        <div>
                          {financialInformation?.rateOverview?.fixedMarkupRate}
                        </div>
                      </Col>
                    )}

                    {!!financialInformation?.rateOverview?.otRateMultiplier && (
                      <Col xs={24} md={12} data-id="customerOtRateMultiplier">
                        <div className="title">OT Rate multiplier:</div>
                        <div>
                          {financialInformation?.rateOverview?.otRateMultiplier}
                        </div>
                      </Col>
                    )}

                    {!!financialInformation?.rateOverview
                      ?.tempToPermConversionFileKey && (
                      <Col
                        xs={24}
                        md={12}
                        data-id="customerTempToPermConversion"
                      >
                        <div className="title">Temp-to-perm:</div>
                        <div>
                          <span
                            className="link-imitation"
                            onClick={handleDownloadTempToPermConversionFile}
                          >
                            Customer {customerKey} temp-to-perm conversion
                          </span>
                        </div>
                      </Col>
                    )}
                  </Row>
                </div>
              )}

              {isMspOrVmsOverviewVisible && (
                <>
                  <Divider className="no-margin" />

                  <div className="content-wrapper">
                    <Row gutter={[4, 4]} className="titled-sheet">
                      <Col xs={24}>
                        <div className="subtitle">MSP/VMS overview</div>
                      </Col>

                      {!isNil(
                        financialInformation?.mspOrVmsOverview?.mspFee,
                      ) && (
                        <Col xs={24} md={12} data-id="customerMspFee">
                          <div className="title">MSP fee:</div>
                          <div>
                            {financialInformation?.mspOrVmsOverview?.mspFee.toFixed(
                              2,
                            )}
                            %
                          </div>
                        </Col>
                      )}

                      {!isNil(
                        financialInformation?.mspOrVmsOverview?.vmsFee,
                      ) && (
                        <Col xs={24} md={12} data-id="customerVmsFee">
                          <div className="title">VMS fee:</div>
                          <div>
                            {financialInformation?.mspOrVmsOverview?.vmsFee.toFixed(
                              2,
                            )}
                            %
                          </div>
                        </Col>
                      )}

                      {!!financialInformation?.mspOrVmsOverview
                        ?.invoicingInformation && (
                        <Col xs={24} md={12} data-id="customerInvoicingInfo">
                          <div className="title">Invoicing information:</div>
                          <div>
                            {
                              financialInformation?.mspOrVmsOverview
                                ?.invoicingInformation
                            }
                          </div>
                        </Col>
                      )}

                      {!!financialInformation?.mspOrVmsOverview
                        ?.specialInvoicingInstructions && (
                        <Col
                          xs={24}
                          md={12}
                          data-id="customerSpecialInvoicingInstructions"
                        >
                          <div className="title">
                            Special invoicing instructions:
                          </div>
                          <div>
                            {
                              financialInformation?.mspOrVmsOverview
                                ?.specialInvoicingInstructions
                            }
                          </div>
                        </Col>
                      )}

                      {!!financialInformation?.mspOrVmsOverview
                        ?.invoicingCycle && (
                        <Col xs={24} md={12} data-id="customerInvoicingCycle">
                          <div className="title">Invoicing cycle:</div>
                          <div>
                            {getCustomerInvoicingCycleLabel(
                              financialInformation?.mspOrVmsOverview
                                ?.invoicingCycle,
                            )}
                          </div>
                        </Col>
                      )}

                      {!!financialInformation?.mspOrVmsOverview
                        ?.payrollCycle && (
                        <Col xs={24} md={12} data-id="customerPayrollCycle">
                          <div className="title">Pay period:</div>
                          <div>
                            {getCustomerPayrollCycleLabel(
                              financialInformation?.mspOrVmsOverview
                                ?.payrollCycle,
                            )}
                          </div>
                        </Col>
                      )}
                      {!isNil(
                        financialInformation?.mspOrVmsOverview
                          ?.isThereThirdPartyWorkforce,
                      ) && (
                        <Col xs={24} md={12}>
                          <div>
                            <div className="title">
                              Is there a third party workforce:
                            </div>
                            {financialInformation?.mspOrVmsOverview
                              ?.isThereThirdPartyWorkforce
                              ? 'Yes'
                              : 'No'}
                          </div>
                        </Col>
                      )}
                      {financialInformation?.mspOrVmsOverview
                        ?.isThereThirdPartyWorkforce && (
                        <>
                          {!isNil(
                            financialInformation?.mspOrVmsOverview?.url,
                          ) && (
                            <Col xs={24} md={12} data-id="customerUrl">
                              <div className="title">URL:</div>
                              <a
                                href={getWebsiteLink(
                                  financialInformation?.mspOrVmsOverview?.url,
                                )}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {getWebsiteName(
                                  financialInformation?.mspOrVmsOverview?.url,
                                )}
                              </a>
                            </Col>
                          )}

                          {!isNil(
                            financialInformation?.mspOrVmsOverview?.credentials,
                          ) && (
                            <Col xs={24} md={12} data-id="customerCredentials">
                              <div className="title">Credentials:</div>
                              <div>
                                {
                                  financialInformation?.mspOrVmsOverview
                                    ?.credentials
                                }
                              </div>
                            </Col>
                          )}

                          {!isNil(
                            financialInformation?.mspOrVmsOverview
                              ?.mspOrVmsProviderName,
                          ) && (
                            <Col xs={24} md={12} data-id="customerProviderName">
                              <div className="title">
                                MSP/VMS provider name:
                              </div>
                              <div>
                                {
                                  financialInformation?.mspOrVmsOverview
                                    ?.mspOrVmsProviderName
                                }
                              </div>
                            </Col>
                          )}
                        </>
                      )}

                      {!isNil(
                        financialInformation?.mspOrVmsOverview?.purchaseOrder,
                      ) && (
                        <Col xs={24} md={12} data-id="customerPurchaseOrder">
                          <div className="title">Purchase order:</div>
                          <div>
                            {financialInformation?.mspOrVmsOverview
                              ?.purchaseOrder
                              ? 'Yes'
                              : 'No'}
                          </div>
                        </Col>
                      )}
                      {!!financialInformation?.mspOrVmsOverview
                        ?.purchaseOrderInstructions && (
                        <Col
                          xs={24}
                          md={12}
                          data-id="customerPurchaseOrderInstructions"
                        >
                          <div className="title">
                            Purchase order instructions:
                          </div>
                          <div>
                            {
                              financialInformation?.mspOrVmsOverview
                                ?.purchaseOrderInstructions
                            }
                          </div>
                        </Col>
                      )}

                      {!!financialInformation?.mspOrVmsOverview
                        ?.purchaseOrderFileKey && (
                        <Col
                          xs={24}
                          md={12}
                          data-id="customerPurchaseOrderFileKey"
                        >
                          <div className="title">Purchase order:</div>
                          <div>
                            <span
                              className="link-imitation"
                              onClick={handleDownloadPurchaseOrderFile}
                            >
                              Customer {customerKey} Purchase order
                            </span>
                          </div>
                        </Col>
                      )}
                    </Row>
                  </div>
                </>
              )}
            </>
          </BasePageCard>
        </Col>
      )}

      {isAdminRole && isNil(additionalInfo3) && (
        <Col xs={24} md={12} className="d-flex">
          <BasePageCard
            className="stretch-card"
            title="Additional information 3"
            icons={<span className="icon icon-writing-tool"></span>}
          >
            <div className="content-wrapper">
              <BaseSectionMessage
                type="info"
                title="Info"
                message={
                  <span>
                    Please fill in the customer additional information.
                  </span>
                }
              />
            </div>
          </BasePageCard>
        </Col>
      )}

      {!isNil(additionalInfo3) && (
        <Col xs={24} md={12} className="d-flex">
          <BasePageCard
            className="stretch-card"
            title="Additional information 3"
            icons={<span className="icon icon-writing-tool"></span>}
          >
            <>
              {isAssignmentRequirementsVisible && (
                <div className="content-wrapper">
                  <Row gutter={[4, 4]} className="titled-sheet">
                    <Col xs={24}>
                      <div className="subtitle">Assignment requirements</div>
                    </Col>

                    {!isNil(
                      additionalInfo3?.assignmentRequirements
                        ?.isIndependentContractors,
                    ) && (
                      <Col
                        xs={24}
                        md={12}
                        data-id="customerIndependentContractorsPermitted"
                      >
                        <div className="title">
                          Independent contractors permitted:
                        </div>
                        <div>
                          {additionalInfo3?.assignmentRequirements
                            ?.isIndependentContractors
                            ? 'Yes'
                            : 'No'}
                        </div>
                      </Col>
                    )}

                    {!isNil(
                      additionalInfo3?.assignmentRequirements
                        ?.designatedPreScreeningProvider,
                    ) && (
                      <Col
                        xs={24}
                        md={12}
                        data-id="customerDesignatedPreScreeningProvider"
                      >
                        <div className="title">
                          Designated pre-screening provider:
                        </div>
                        <div>
                          {additionalInfo3?.assignmentRequirements
                            ?.designatedPreScreeningProvider
                            ? 'Yes'
                            : 'No'}
                        </div>
                      </Col>
                    )}

                    {!isNil(
                      additionalInfo3?.assignmentRequirements
                        ?.isPreScreeningRequired,
                    ) && (
                      <Col
                        xs={24}
                        md={12}
                        data-id="customerPreScreeningRequired"
                      >
                        <div className="title">Pre-screening required:</div>
                        <div>
                          {additionalInfo3?.assignmentRequirements
                            ?.isPreScreeningRequired
                            ? 'Yes'
                            : 'No'}
                        </div>
                      </Col>
                    )}

                    {!isNil(
                      additionalInfo3?.assignmentRequirements
                        ?.preScreeningBilledTo,
                    ) && (
                      <Col
                        xs={24}
                        md={12}
                        data-id="customerPreScreeningBilledTo"
                      >
                        <div className="title">Pre-screening billed to:</div>
                        <div>
                          {getCustomerPreScreeningBilledLabel(
                            additionalInfo3?.assignmentRequirements
                              ?.preScreeningBilledTo,
                          )}
                        </div>
                      </Col>
                    )}

                    {!isNil(
                      additionalInfo3?.assignmentRequirements?.preScreening,
                    ) && (
                      <Col xs={24} md={12} data-id="customerPreScreening">
                        <div className="title">Pre-screening:</div>
                        <div>
                          {
                            additionalInfo3?.assignmentRequirements
                              ?.preScreening
                          }
                        </div>
                      </Col>
                    )}

                    {!isNil(
                      additionalInfo3?.assignmentRequirements
                        ?.isOnboardingDocumentationRequired,
                    ) && (
                      <Col
                        xs={24}
                        md={12}
                        data-id="customerOnboardingDocumentationRequired"
                      >
                        <div className="title">
                          Onboarding documentation required:
                        </div>
                        <div>
                          {additionalInfo3?.assignmentRequirements
                            ?.isOnboardingDocumentationRequired
                            ? 'Yes'
                            : 'No'}
                        </div>
                      </Col>
                    )}

                    {!!additionalInfo3?.assignmentRequirements
                      ?.specialInstructions && (
                      <Col
                        xs={24}
                        md={12}
                        data-id="customerSpecialInstructions"
                      >
                        <div className="title">Special instructions:</div>
                        <div>
                          {
                            additionalInfo3?.assignmentRequirements
                              .specialInstructions
                          }
                        </div>
                      </Col>
                    )}

                    {!!additionalInfo3.assignmentRequirements
                      ?.onboardingDocumentationFileKeys?.length && (
                      <Col
                        xs={24}
                        md={12}
                        data-id="customerOnboardingDocumentation"
                      >
                        <div className="title">Onboarding documentation:</div>
                        <div>
                          {additionalInfo3.assignmentRequirements.onboardingDocumentationFileKeys
                            .map<React.ReactNode>((key, index) => (
                              <span
                                key={index}
                                className="link-imitation"
                                onClick={() =>
                                  handleDownloadOnboardingDocumentationFile(key)
                                }
                              >
                                Customer {customerKey} onboarding document{' '}
                                {index + 1}
                              </span>
                            ))
                            .reduce((previous, current) => [
                              previous,
                              ', ',
                              current,
                            ])}
                        </div>
                      </Col>
                    )}

                    {!!additionalInfo3?.assignmentRequirements
                      ?.terminationInstructions && (
                      <Col
                        xs={24}
                        md={12}
                        data-id="customerTerminationInstructions"
                      >
                        <div className="title">Termination instructions:</div>
                        <div>
                          {
                            additionalInfo3?.assignmentRequirements
                              .terminationInstructions
                          }
                        </div>
                      </Col>
                    )}
                  </Row>
                </div>
              )}

              {isSpecialRequirementsVisible && (
                <>
                  <Divider className="no-margin" />

                  <div className="content-wrapper">
                    <Row gutter={[4, 4]} className="titled-sheet">
                      <Col xs={24}>
                        <div className="subtitle">
                          Special assignment requirements
                        </div>
                      </Col>

                      {!!additionalInfo3?.specialAssignmentRequirements
                        ?.guaranteedWorkPerformance && (
                        <Col
                          xs={24}
                          md={12}
                          data-id="customerGuaranteedWorkPerformance"
                        >
                          <div className="title">
                            Guaranteed work performance:
                          </div>
                          <div>
                            {
                              additionalInfo3?.specialAssignmentRequirements
                                .guaranteedWorkPerformance
                            }
                          </div>
                        </Col>
                      )}

                      {!!additionalInfo3?.specialAssignmentRequirements
                        ?.tempToPermConversion && (
                        <Col
                          xs={24}
                          md={12}
                          data-id="customerTempToPermConversion"
                        >
                          <div className="title">Temp-to-perm conversion:</div>
                          <div>
                            {
                              additionalInfo3?.specialAssignmentRequirements
                                .tempToPermConversion
                            }
                          </div>
                        </Col>
                      )}

                      {!!additionalInfo3?.specialAssignmentRequirements
                        ?.timeEntry && (
                        <Col xs={24} md={12} data-id="customerTimeEntry">
                          <div className="title">Time entry method:</div>
                          <div>
                            {
                              additionalInfo3?.specialAssignmentRequirements
                                .timeEntry
                            }
                          </div>
                        </Col>
                      )}

                      {!!additionalInfo3?.specialAssignmentRequirements
                        ?.addedNotes && (
                        <Col
                          xs={24}
                          md={12}
                          data-id="customerAssignmentAddedNotes"
                        >
                          <div className="title">Added notes:</div>
                          <div>
                            {
                              additionalInfo3?.specialAssignmentRequirements
                                .addedNotes
                            }
                          </div>
                        </Col>
                      )}

                      {!isNil(
                        additionalInfo3?.specialAssignmentRequirements
                          ?.breakInService,
                      ) && (
                        <Col xs={24} md={12} data-id="customerBreakInService">
                          <div className="title">Break in service:</div>
                          <div>
                            {getCustomerBreakInServiceLabel(
                              additionalInfo3?.specialAssignmentRequirements
                                ?.breakInService as BreakInServiceEnum,
                            )}
                          </div>
                        </Col>
                      )}
                    </Row>
                  </div>
                </>
              )}
            </>
          </BasePageCard>
        </Col>
      )}
    </Row>
  );
}
